/**
 * useTourSpotlight
 *
 * Highlights a tour target in the DOM with a measured rectangle.
 * Shared between modules to replace module-specific spotlight logic.
 */

'use client';

import { useEffect, useState, useCallback, type MutableRefObject } from 'react';

export interface SpotlightRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export type FocusTargetState = 'idle' | 'searching' | 'ready' | 'missing';

interface UseTourSpotlightOptions {
  focusTarget: string | null | undefined;
  /**
   * Optional secondary target that "reveals" itself after the user interacts
   * with `focusTarget` (e.g. a modal). When the element matching
   * `[data-tour-id="<revealTarget>"]` is visible, the spotlight prefers it.
   * See `spotlightReveal` for ring behaviour.
   */
  revealTarget?: string | null | undefined;
  /**
   * Only meaningful with `revealTarget`. If true, draw the spotlight ring
   * around the revealed element. If false (default), clear the ring when the
   * reveal element appears (the tour card alone stays visible — useful for
   * full-screen modals where a viewport-sized ring is meaningless).
   */
  spotlightReveal?: boolean;
  hasSelectedFlow: boolean;
  isOpen: boolean;
  isMinimized: boolean;
  isObjectiveIndexMode?: boolean;
  isCompactMobile?: boolean;
  hasScrolledToTargetRef?: MutableRefObject<boolean>;
}

export function resolveTourTarget(selector: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.querySelector<HTMLElement>(selector);
}

export function measureSpotlight(element: HTMLElement): SpotlightRect {
  const rect = element.getBoundingClientRect();

  return {
    left: rect.left,
    right: rect.right,
    top: rect.top,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
}

export function useTourSpotlight({
  focusTarget,
  revealTarget,
  spotlightReveal = false,
  hasSelectedFlow,
  isOpen,
  isMinimized,
  isObjectiveIndexMode = false,
  isCompactMobile = false,
  hasScrolledToTargetRef,
}: UseTourSpotlightOptions) {
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [focusTargetState, setFocusTargetState] = useState<FocusTargetState>('idle');

  const setHasScrolled = useCallback(
    (value: boolean) => {
      if (hasScrolledToTargetRef) {
        hasScrolledToTargetRef.current = value;
      }
    },
    [hasScrolledToTargetRef]
  );

  const resetSpotlight = useCallback(() => {
    setSpotlightRect(null);
    setFocusTargetState('idle');
    setHasScrolled(false);
  }, [setHasScrolled]);

  useEffect(() => {
    if (
      !isOpen ||
      isMinimized ||
      isObjectiveIndexMode ||
      !hasSelectedFlow ||
      !focusTarget ||
      typeof window === 'undefined'
    ) {
      // Defer the reset into a microtask to avoid cascading renders.
      queueMicrotask(resetSpotlight);
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    let mutationObserver: MutationObserver | null = null;
    // The legacy AI onboarding coach resolved regions via [data-region] OR
    // [data-tour-id]; keep both so migrated coach regions still spotlight.
    // focusTarget may declare ordered fallbacks ("primary | fallback") — the
    // first candidate present in the DOM wins, so a generic container can
    // backstop a widget that only renders under some conditions.
    const focusSelectors = focusTarget
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean)
      .flatMap((t) => [`[data-tour-id="${t}"]`, `[data-region="${t}"]`]);
    // revealTarget supports the same ordered "a | b" fallbacks as
    // focusTarget — useful when a step can reveal one of several surfaces
    // (e.g. a populated list or an empty-state panel).
    const revealSelectors = revealTarget
      ? revealTarget
          .split('|')
          .map((t) => t.trim())
          .filter(Boolean)
          .flatMap((t) => [`[data-tour-id="${t}"]`, `[data-region="${t}"]`])
      : [];

    queueMicrotask(() => setFocusTargetState('searching'));

    // When a reveal target is defined, prefer it once it is visible. This is
    // what lets a step re-target onto a modal that the user just opened.
    const resolveEffectiveElement = (): { element: HTMLElement; isReveal: boolean } | null => {
      for (const sel of revealSelectors) {
        const revealEl = resolveTourTarget(sel);
        if (revealEl && revealEl.checkVisibility()) {
          const r = measureSpotlight(revealEl);
          if (r.width > 0 && r.height > 0) {
            return { element: revealEl, isReveal: true };
          }
        }
      }

      for (const sel of focusSelectors) {
        const focusEl = resolveTourTarget(sel);
        if (focusEl) {
          return { element: focusEl, isReveal: false };
        }
      }
      return null;
    };

    // Value-equality wrapper for setSpotlightRect — prevents unnecessary
    // re-renders when the measured rect hasn't actually changed (e.g. when
    // a MutationObserver fires but the element's position is identical).
    const updateRect = (next: SpotlightRect | null) => {
      setSpotlightRect((prev) => {
        if (prev && next) {
          if (
            prev.left === next.left &&
            prev.right === next.right &&
            prev.top === next.top &&
            prev.bottom === next.bottom &&
            prev.width === next.width &&
            prev.height === next.height
          ) {
            return prev;
          }
        }
        return next;
      });
    };

    const syncSpotlight = () => {
      if (cancelled) {
        return;
      }

      const effective = resolveEffectiveElement();
      if (!effective) {
        if (attempts < 180) {
          attempts += 1;
          rafId = window.requestAnimationFrame(syncSpotlight);
          return;
        }

        updateRect(null);
        setFocusTargetState('missing');
        return;
      }

      const measuredRect = measureSpotlight(effective.element);
      if (measuredRect.width <= 0 || measuredRect.height <= 0) {
        if (attempts < 180) {
          attempts += 1;
          rafId = window.requestAnimationFrame(syncSpotlight);
          setFocusTargetState('searching');
          return;
        }

        updateRect(null);
        setFocusTargetState('missing');
        return;
      }

      // If the reveal element is the effective target but we don't want a
      // ring around it (e.g. a full-screen modal), clear the spotlight rect
      // so only the tour card remains visible above the modal.
      if (effective.isReveal && !spotlightReveal) {
        updateRect(null);
        setFocusTargetState('ready');
        return;
      }

      if (!effective.isReveal && !hasScrolledToTargetRef?.current) {
        setHasScrolled(true);
        effective.element.scrollIntoView({
          behavior: 'smooth',
          block: isCompactMobile ? 'start' : 'center',
          inline: 'center',
        });
      }

      updateRect(measuredRect);
      setFocusTargetState('ready');
    };

    const handleRecalculate = () => {
      if (cancelled) {
        return;
      }

      const effective = resolveEffectiveElement();
      if (!effective) {
        updateRect(null);
        setFocusTargetState('missing');
        return;
      }

      const measuredRect = measureSpotlight(effective.element);
      if (measuredRect.width > 0 && measuredRect.height > 0) {
        if (effective.isReveal && !spotlightReveal) {
          updateRect(null);
        } else {
          updateRect(measuredRect);
        }
        setFocusTargetState('ready');
        return;
      }

      setFocusTargetState('searching');
    };

    rafId = window.requestAnimationFrame(() => {
      rafId = window.requestAnimationFrame(syncSpotlight);
    });

    if (typeof MutationObserver !== 'undefined' && document.body) {
      mutationObserver = new MutationObserver(() => {
        if (cancelled) {
          return;
        }

        window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(syncSpotlight);
      });
      mutationObserver.observe(document.body, {
        attributes: true,
        // NOTE: 'style' is intentionally excluded — the spotlight overlay
        // itself changes style (left/top/width/height) on every re-render,
        // which would re-trigger this observer and create a feedback loop.
        attributeFilter: ['class', 'data-tour-id', 'data-region', 'hidden'],
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener('resize', handleRecalculate);
    window.addEventListener('scroll', handleRecalculate, true);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      mutationObserver?.disconnect();
      window.removeEventListener('resize', handleRecalculate);
      window.removeEventListener('scroll', handleRecalculate, true);
    };
  }, [
    focusTarget,
    revealTarget,
    spotlightReveal,
    hasSelectedFlow,
    isCompactMobile,
    isMinimized,
    isObjectiveIndexMode,
    isOpen,
    resetSpotlight,
    setHasScrolled,
    hasScrolledToTargetRef,
  ]);

  return {
    spotlightRect,
    focusTargetState,
    setSpotlightRect,
    resetSpotlight,
  };
}
